// Spreadsheet-style grid on Glide Data Grid (design flag 2): inline edit, paste from Excel, keyboard nav, frozen columns.
import "@glideapps/glide-data-grid/dist/index.css";
import {
  DataEditor,
  GridCellKind,
  CompactSelection,
  type EditableGridCell,
  type GridCell,
  type GridColumn,
  type GridSelection,
  type Item,
  type ProvideEditorCallback,
  type ProvideEditorComponent,
  type TextCell,
  type Theme,
} from "@glideapps/glide-data-grid";
import { CalendarDays } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fmtDate } from "./ui";
import { CalendarPopup } from "./CalendarPopup";
import { useDark } from "@/lib/theme";
import { parseDate } from "@/engine/dates";

export type ColKind = "text" | "number" | "date" | "bool" | "pct";

export interface Col<R> {
  id: string;
  title: string;
  width?: number;
  group?: string;
  kind?: ColKind;
  get: (r: R) => string | number | boolean | null | undefined;
  /** When set, the cell is editable. Return a value to write; throw for invalid input. */
  set?: (r: R, v: string | number | boolean | null) => void;
  /** Allowed values for text cells (validated on edit and paste). */
  options?: string[];
  readonly?: (r: R) => boolean;
  /** Cell highlight. */
  tone?: (r: R) => "error" | "warn" | "muted" | "derived" | undefined;
  digits?: number;
}

export interface CellEdit<R> {
  row: R;
  col: Col<R>;
  value: string | number | boolean | null;
}

const base: Partial<Theme> = {
  headerFontStyle: "600 12px",
  baseFontStyle: "12.5px",
  fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
  cellHorizontalPadding: 6,
};

// The grid draws on a canvas, so it needs its own light and dark colours (the page CSS can't reach it).
const THEMES = {
  light: {
    theme: {
      ...base,
      accentColor: "#2d8b7f",
      accentLight: "rgba(45,139,127,0.14)",
      bgHeader: "#f1f5f9",
      bgHeaderHasFocus: "#e2e8f0",
      textHeader: "#334155",
      borderColor: "rgba(15,23,42,0.08)",
    } as Partial<Theme>,
    tone: { error: "#fee2e2", warn: "#fef3c7", muted: "#f8fafc", derived: "#eef4fb" },
    readonly: { bgCell: "#fafafa", textDark: "#475569" },
  },
  dark: {
    theme: {
      ...base,
      accentColor: "#3aa596",
      accentFg: "#0e1525",
      accentLight: "rgba(58,165,150,0.22)",
      textDark: "#e6eaf0",
      textMedium: "#b6bfcd",
      textLight: "#7d89a0",
      textBubble: "#e6eaf0",
      bgIconHeader: "#9aa5b8",
      fgIconHeader: "#151d2e",
      textHeader: "#b6bfcd",
      textGroupHeader: "#b6bfcd",
      textHeaderSelected: "#0e1525",
      bgCell: "#151d2e",
      bgCellMedium: "#1c2537",
      bgHeader: "#1c2537",
      bgHeaderHasFocus: "#263044",
      bgHeaderHovered: "#263044",
      bgBubble: "#263044",
      bgBubbleSelected: "#364156",
      bgSearchResult: "#3a2c12",
      borderColor: "rgba(230,234,240,0.09)",
      drilldownBorder: "rgba(230,234,240,0.2)",
      linkColor: "#7fd1c4",
    } as Partial<Theme>,
    tone: { error: "#3b1d22", warn: "#3a2c12", muted: "#111827", derived: "#1b3350" },
    readonly: { bgCell: "#111827", textDark: "#9aa5b8" },
  },
};

const MARKER = 44;

export function toValue(kind: ColKind | undefined, raw: string): string | number | boolean | null {
  const t = raw.trim();
  if (!t) return null;
  if (kind === "number" || kind === "pct") {
    const n = Number(t.replace(/,/g, "").replace(/%$/, ""));
    if (!isFinite(n)) throw new Error(`"${raw}" is not a number`);
    return kind === "pct" && t.endsWith("%") ? n / 100 : n;
  }
  if (kind === "date") {
    const d = parseDate(/^\d+(\.\d+)?$/.test(t) ? Number(t) : t);
    if (!d) throw new Error(`"${raw}" is not a date`);
    return d;
  }
  if (kind === "bool") return ["y", "yes", "true", "1", "x"].includes(t.toLowerCase());
  return t;
}

/** Marks text cells that hold dates, so the overlay editor can offer a calendar. */
const DATE_CELL = "__date";

// Date cell editor: type a date (15-Jul-26, pasted Excel values, ...) or pick one from the calendar.
// Opening the cell with a double-click or Enter shows the calendar straight away; typing a character skips it.
// Only a click on a day picks a date; moving between months doesn't (the browser's picker did, Cesar Oct 16).
const DateEditor: ProvideEditorComponent<TextCell> = ({ value, initialValue, onChange, onFinishedEditing }) => {
  const [text, setText] = useState(initialValue ?? (value.data ? fmtDate(value.data) : ""));
  const box = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(initialValue === undefined);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    // The grid positions its overlay after the first paint (the first time, it starts at the page's top-left).
    // Follow the editor until it stops moving, then place the calendar under it (or above, near the bottom).
    let frame = 0;
    let last = "";
    let tries = 0;
    const tick = () => {
      const r = box.current?.getBoundingClientRect();
      if (r) {
        const H = 290;
        const top = r.bottom + 4 + H > window.innerHeight ? Math.max(4, r.top - H - 4) : r.bottom + 4;
        const left = Math.min(r.left, window.innerWidth - 264);
        setPos({ left, top });
        const key = `${Math.round(r.left)},${Math.round(r.top)}`;
        if ((r.left > 0 || r.top > 0) && key === last) return;
        last = key;
      }
      if (++tries < 30) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [open]);
  const pick = (iso: string | null) => onFinishedEditing({ ...value, data: iso ?? "" }, [0, 0]);
  return (
    <div ref={box} className="relative flex items-center gap-1" style={{ minWidth: 150 }}>
      <input
        autoFocus
        className="w-full bg-transparent px-1 text-[12.5px] outline-none"
        value={text}
        placeholder="dd-Mmm-yy"
        onChange={(e) => {
          setText(e.target.value);
          onChange({ ...value, data: e.target.value });
        }}
      />
      <button type="button" title="Pick a date" aria-label="Pick a date" className="shrink-0 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800" onClick={() => setOpen((o) => !o)}>
        <CalendarDays size={15} />
      </button>
      {open &&
        pos &&
        createPortal(<CalendarPopup value={parseDate(text)} onPick={pick} onClear={() => pick(null)} className="fixed z-[1000]" style={{ left: pos.left, top: pos.top }} />, document.body)}
    </div>
  );
};

const provideEditor: ProvideEditorCallback<GridCell> = (cell) =>
  (cell as { [DATE_CELL]?: boolean })[DATE_CELL] ? ({ editor: DateEditor } as ReturnType<ProvideEditorCallback<GridCell>>) : undefined!;

export function DataGrid<R>({
  rows,
  cols,
  onEdit,
  onError,
  height = 520,
  freeze = 1,
  onSelectRows,
  rowMarkers = true,
  headerHeight,
  onCellClick,
}: {
  rows: R[];
  cols: Col<R>[];
  onEdit?: (edits: CellEdit<R>[]) => void | Promise<unknown>;
  onError?: (msg: string) => void;
  height?: number | string;
  freeze?: number;
  onSelectRows?: (rows: R[]) => void;
  rowMarkers?: boolean;
  headerHeight?: number;
  onCellClick?: (row: R, col: Col<R>) => void;
}) {
  const [sel, setSel] = useState<GridSelection>({ rows: CompactSelection.empty(), columns: CompactSelection.empty() });
  const [widths, setWidths] = useState<Record<string, number>>({});
  const t = THEMES[useDark() ? "dark" : "light"];

  const columns: GridColumn[] = useMemo(
    () => cols.map((c) => ({ id: c.id, title: c.title, width: widths[c.id] ?? c.width ?? 110, group: c.group })),
    [cols, widths],
  );

  const getCellContent = useCallback(
    ([ci, ri]: Item): GridCell => {
      const c = cols[ci];
      const r = rows[ri];
      const v = r === undefined ? null : c.get(r);
      const editable = !!c.set && !(c.readonly?.(r) ?? false);
      const tone = r === undefined ? undefined : c.tone?.(r);
      const themeOverride = tone ? { bgCell: t.tone[tone] } : editable ? undefined : t.readonly;
      if (c.kind === "bool")
        return { kind: GridCellKind.Boolean, data: !!v, allowOverlay: false, readonly: !editable, themeOverride };
      if (c.kind === "number" || c.kind === "pct") {
        const n = typeof v === "number" ? v : null;
        const d = c.digits ?? (c.kind === "pct" ? 1 : 0);
        return {
          kind: GridCellKind.Number,
          data: n ?? undefined,
          displayData: n === null ? "" : c.kind === "pct" ? `${(n * 100).toFixed(d)}%` : n.toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d }),
          allowOverlay: editable,
          readonly: !editable,
          contentAlign: "right",
          themeOverride,
        };
      }
      const s = v === null || v === undefined ? "" : String(v);
      return {
        kind: GridCellKind.Text,
        data: s,
        displayData: c.kind === "date" ? fmtDate(s) : s,
        allowOverlay: editable,
        readonly: !editable,
        themeOverride,
        ...(c.kind === "date" && { [DATE_CELL]: true }),
      } as GridCell;
    },
    [cols, rows, t],
  );

  const apply = useCallback(
    (items: readonly { location: Item; value: EditableGridCell }[]) => {
      if (!onEdit) return false;
      const edits: CellEdit<R>[] = [];
      const errors: string[] = [];
      for (const { location, value } of items) {
        const [ci, ri] = location;
        const c = cols[ci];
        const r = rows[ri];
        if (!c || r === undefined || !c.set || c.readonly?.(r)) continue;
        let raw: string;
        if (value.kind === GridCellKind.Boolean) raw = value.data ? "true" : "";
        else if (value.kind === GridCellKind.Number) raw = value.data === undefined ? "" : String(value.data);
        else raw = String((value as { data?: unknown }).data ?? "");
        try {
          const v = toValue(c.kind, raw);
          if (c.options && v !== null && !c.options.includes(String(v))) {
            const hit = c.options.find((o) => o.toLowerCase() === String(v).toLowerCase());
            if (!hit) throw new Error(`"${v}" is not a valid ${c.title}`);
            edits.push({ row: r, col: c, value: hit });
            continue;
          }
          edits.push({ row: r, col: c, value: v });
        } catch (e) {
          errors.push(`Row ${ri + 1}, ${c.title}: ${(e as Error).message}`);
        }
      }
      if (errors.length) onError?.(errors.slice(0, 8).join("\n"));
      if (edits.length) void onEdit(edits);
      return true;
    },
    [cols, rows, onEdit, onError],
  );

  return (
    <div
      className="overflow-hidden rounded-lg border border-slate-200 bg-surface"
      style={{ height }}
      data-testid="grid"
      data-layout={JSON.stringify({ marker: rowMarkers ? MARKER : 0, header: (headerHeight ?? 32) + (cols.some((c) => c.group) ? 28 : 0), row: 30, cols: columns.map((c) => [c.id, (c as { width: number }).width]) })}
    >
      <DataEditor
        theme={t.theme}
        columns={columns}
        rows={rows.length}
        getCellContent={getCellContent}
        getCellsForSelection
        onCellEdited={(location, value) => apply([{ location, value }])}
        onCellClicked={
          onCellClick
            ? ([ci, ri]) => {
                const r = rows[ri];
                if (r !== undefined && cols[ci]) onCellClick(r, cols[ci]);
              }
            : undefined
        }
        onCellsEdited={(items) => apply(items)}
        onPaste
        provideEditor={provideEditor}
        freezeColumns={freeze}
        // Checkboxes always show where rows can be selected; on hover only, nobody found them (Cesar, Oct 16).
        rowMarkers={!rowMarkers ? "none" : onSelectRows ? "checkbox-visible" : "number"}
        rowMarkerWidth={MARKER}
        smoothScrollX
        smoothScrollY
        width="100%"
        height="100%"
        headerHeight={headerHeight ?? 32}
        groupHeaderHeight={28}
        rowHeight={30}
        gridSelection={sel}
        onGridSelectionChange={(s) => {
          setSel(s);
          if (onSelectRows) onSelectRows(s.rows.toArray().map((i) => rows[i]).filter((x) => x !== undefined));
        }}
        onColumnResize={(col, w) => setWidths((x) => ({ ...x, [col.id as string]: w }))}

      />
    </div>
  );
}
